import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Building2, Clock, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	type SubscriptionScope,
	scopeLabelKey,
	statusLabelKey,
	statusToVariant,
} from "@/lib/subscription-display";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/organizations/")({
	component: OrganizationsPage,
});

type Organization = {
	id: string;
	name: string;
	logo: string | null;
	billingCustomerId: string | null;
	retentionStartedAt: Date | null;
	retentionEndsAt: Date | null;
	createdAt: Date;
	updatedAt: Date;
	memberCount: number;
	projectCount: number;
	seatScope: SubscriptionScope | null;
	seatStatus: string | null;
	seatPlanItemPriceId: string | null;
	projectLicenseCount: number;
	hasPendingChange: boolean;
};

function OrganizationsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
	const [showDetailsPanelOnMobile, setShowDetailsPanelOnMobile] =
		useState(false);

	// Debounce search input
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);

		return () => clearTimeout(timer);
	}, [search]);

	// Fetch organizations
	const queryKey = [
		"admin",
		"organizations",
		"list",
		{ search: debouncedSearch, limit: 50, offset: 0 },
	];

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			const result = await client.admin.organizations.listOrganizations({
				search: debouncedSearch || undefined,
				limit: 50,
				offset: 0,
			});
			return result;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});

	const navigateToDetails = (orgId: string) => {
		navigate({ to: `/organizations/${orgId}` });
	};

	const renderSubscriptionSummary = (org: {
		seatScope: SubscriptionScope | null;
		seatStatus: string | null;
		projectLicenseCount: number;
		hasPendingChange: boolean;
	}) => {
		const hasAny = org.seatStatus !== null || org.projectLicenseCount > 0;
		if (!hasAny) {
			return (
				<Badge variant="outline">
					{t("admin:organizations.no_subscription")}
				</Badge>
			);
		}
		return (
			<div className="flex flex-wrap items-center gap-1">
				{org.seatScope && org.seatStatus && (
					<Badge variant={statusToVariant(org.seatStatus)}>
						{t(scopeLabelKey(org.seatScope))}:{" "}
						{t(statusLabelKey(org.seatStatus))}
					</Badge>
				)}
				{org.projectLicenseCount > 0 && (
					<Badge variant="secondary">
						{t("admin:organizations.subscriptions.project_license_count", {
							count: org.projectLicenseCount,
						})}
					</Badge>
				)}
				{org.hasPendingChange && (
					<Badge
						variant="outline"
						title={t(
							"admin:organizations.subscriptions.scheduled_change.badge",
						)}
					>
						<Clock className="h-3 w-3" />
					</Badge>
				)}
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">{t("admin:organizations.title")}</h1>
				<p className="text-muted-foreground">
					{t("admin:organizations.subtitle")}
				</p>
			</div>

			<div className="flex h-[calc(100vh-12rem)] gap-4 overflow-hidden rounded-lg border bg-card shadow-sm">
				{/* Main content area */}
				<div
					className={`flex-1 overflow-hidden ${showDetailsPanelOnMobile ? "hidden md:block" : ""}`}
				>
					<div className="border-b p-4">
						<Input
							placeholder={t("admin:organizations.search_placeholder")}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="max-w-md"
						/>
					</div>

					<ScrollArea className="h-[calc(100%-5rem)]">
						<div className="p-4">
							{isLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-16 w-full" />
										),
									)}
								</div>
							) : data && data.organizations.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("admin:organizations.name")}</TableHead>
											<TableHead>{t("admin:organizations.members")}</TableHead>
											<TableHead>{t("admin:organizations.projects")}</TableHead>
											<TableHead>
												{t("admin:organizations.subscription")}
											</TableHead>
											<TableHead>{t("admin:organizations.created")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.organizations.map((org: Organization) => (
											<TableRow
												key={org.id}
												onClick={() => {
													setSelectedOrg(org);
													setShowDetailsPanelOnMobile(true);
												}}
												onDoubleClick={() => navigateToDetails(org.id)}
												className={`cursor-pointer ${selectedOrg?.id === org.id ? "bg-muted" : ""}`}
											>
												<TableCell>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															navigateToDetails(org.id);
														}}
														className="flex items-center gap-3 text-left"
													>
														{org.logo ? (
															<img
																src={org.logo}
																alt={org.name}
																className="h-8 w-8 rounded object-cover"
															/>
														) : (
															<div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
																<Building2 className="h-4 w-4 text-muted-foreground" />
															</div>
														)}
														<div className="font-medium hover:underline">
															{org.name}
														</div>
													</button>
												</TableCell>
												<TableCell>{org.memberCount}</TableCell>
												<TableCell>{org.projectCount}</TableCell>
												<TableCell>{renderSubscriptionSummary(org)}</TableCell>
												<TableCell className="text-muted-foreground text-sm">
													{new Date(org.createdAt).toLocaleDateString()}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:organizations.no_organizations_found")}
									</p>
								</div>
							)}
						</div>
					</ScrollArea>

					{data && data.total > 0 && (
						<div className="border-t p-4">
							<p className="text-muted-foreground text-sm">
								{t("admin:organizations.showing_organizations", {
									count: data.organizations.length,
									total: data.total,
								})}
							</p>
						</div>
					)}
				</div>

				{/* Sidebar */}
				<div
					className={`w-full border-l bg-muted/20 md:w-72 lg:w-80 ${showDetailsPanelOnMobile ? "block" : "hidden md:block"}`}
				>
					<ScrollArea className="h-full">
						<div className="p-3 md:p-4">
							{selectedOrg ? (
								<>
									<div className="mb-4 flex items-start gap-2 md:gap-3">
										{selectedOrg.logo ? (
											<img
												src={selectedOrg.logo}
												alt={selectedOrg.name}
												className="h-16 w-16 rounded object-cover"
											/>
										) : (
											<div className="flex h-16 w-16 items-center justify-center rounded bg-muted">
												<Building2 className="h-8 w-8 text-muted-foreground" />
											</div>
										)}
										<div className="min-w-0 flex-1">
											<h3 className="font-semibold text-lg">
												{selectedOrg.name}
											</h3>
										</div>
										<Button
											variant="ghost"
											size="sm"
											className="h-8 w-8 p-0 md:hidden"
											onClick={() => setShowDetailsPanelOnMobile(false)}
											title="Close"
										>
											<X className="h-4 w-4" />
										</Button>
									</div>

									<Separator />

									<div className="mt-4 space-y-4">
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin:organizations.quick_info.members")}
											</span>
											<span className="font-medium">
												{selectedOrg.memberCount}
											</span>
										</div>

										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin:organizations.quick_info.projects")}
											</span>
											<span className="font-medium">
												{selectedOrg.projectCount}
											</span>
										</div>

										<div className="flex flex-col gap-1 text-sm">
											<span className="text-muted-foreground">
												{t("admin:organizations.quick_info.subscription")}
											</span>
											<div>{renderSubscriptionSummary(selectedOrg)}</div>
										</div>

										{selectedOrg.seatPlanItemPriceId && (
											<div className="flex flex-col gap-1 text-sm">
												<span className="text-muted-foreground">
													{t("admin:organizations.quick_info.plan")}
												</span>
												<span className="font-medium">
													{selectedOrg.seatPlanItemPriceId}
												</span>
											</div>
										)}

										<div className="flex flex-col gap-1 text-sm">
											<span className="text-muted-foreground">
												{t("admin:organizations.quick_info.created")}
											</span>
											<span className="font-medium">
												{new Date(selectedOrg.createdAt).toLocaleDateString()}
											</span>
										</div>
									</div>
								</>
							) : (
								<div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
									<Building2 className="mb-4 h-12 w-12" />
									<p className="text-sm">
										{t("admin:organizations.quick_info.select_hint")}
									</p>
								</div>
							)}
						</div>
					</ScrollArea>
				</div>
			</div>
		</div>
	);
}
